/**
 * Place Detail Module — full place detail card with photos, map, reviews,
 * editable description/notes/rating, action buttons, and an artwork mode for
 * minimal art-piece rendering.
 */

import React, { useState, useRef, useEffect } from "react";
import type { Module } from "@/types/modules";
import { useSession } from "@/features/auth/hooks/useSession";
import { useUpdateObject } from "@/features/objects/hooks/mutations";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { http } from "@/lib/http";
import { api } from "@/lib/api";
import { useIsCompactLayout } from "@/common/hooks";
import { useToast } from "@/app/providers/ToastContext";
import "../_infrastructure/ModuleStyles.css";
import PlaceCard from "./PlaceCard";
import { PlacePhotosView } from "./components/PlacePhotosView";
import { PlaceReviewsView } from "./components/PlaceReviewsView";
import { PlaceDirectionsView } from "./components/PlaceDirectionsView";

interface PlaceDetailModuleProps {
  module: Module;
  conversationId?: string;
  onClose?: () => void;
  onNoteSaved?: (noteText: string) => void;
  [legacyProp: string]: unknown;
}

interface PlaceData {
  objectId?: string;
  etag?: string;
  placeId?: string;
  isArtwork?: boolean;
  galleryPhotos?: Array<{
    fullsizeUrl: string;
    thumbnailUrl?: string;
    caption?: string;
    isMap?: boolean;
  }>;
  title: string;
  subtitle?: string;
  chips?: string[];
  images?: Array<{
    fullsizeUrl: string;
    thumbnailUrl?: string;
    caption?: string;
    isMap?: boolean;
  }>;
  sections?: Array<{
    title: string;
    body?: string;
    items?: Array<{
      label?: string;
      value: string;
    }>;
  }>;
  actions?: Array<{
    kind: string;
    url: string;
    label: string;
  }>;
  citations?: Array<{
    title?: string;
    url: string;
  }>;
  userNotes?: string;
  userRating?: number;
  imageUrl?: string;
}

export default function PlaceDetailModule(props: PlaceDetailModuleProps) {
  const {
    module,
    conversationId: explicitConversationId,
    onClose,
    onNoteSaved,
  } = props;
  const conversationId = explicitConversationId ?? "";
  const { token } = useSession();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isMobile = useIsCompactLayout();

  const [currentView, setCurrentView] = useState<
    "main" | "photos" | "reviews" | "directions"
  >("main");
  const [photoModalIndex, setPhotoModalIndex] = useState(0);
  const [isMapModalOpen, setMapModalOpen] = useState(false);

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState("");

  const [localRating, setLocalRating] = useState<number | undefined>(undefined);
  const [ratingSaved, setRatingSaved] = useState(false);
  const ratingTouchedRef = useRef(false); // prevents cache re-render from overwriting user's drag
  const ratingSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // debounce rating save

  const [copiedLink, setCopiedLink] = useState(false);

  const [showGearMenu, setShowGearMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const gearMenuRef = useRef<HTMLDivElement>(null);

  // Close gear menu on outside click
  useEffect(() => {
    if (!showGearMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (
        gearMenuRef.current &&
        !gearMenuRef.current.contains(e.target as Node)
      ) {
        setShowGearMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showGearMenu]);

  const thumbnailStripRef = React.useRef<HTMLDivElement>(null);
  const thumbnailRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const placeData = (module.config as PlaceData) || {};

  // Sync local rating from placeData on mount — skip if user has touched the slider
  useEffect(() => {
    if (ratingTouchedRef.current) return;
    if ((placeData as any).userRating != null) {
      setLocalRating((placeData as any).userRating);
    }
  }, [(placeData as any).userRating]);

  // ALWAYS call hooks unconditionally - never conditionally
  const updateMutation = useUpdateObject();

  const handleAction = (action: {
    kind: string;
    url: string;
    label: string;
  }) => {
    if (action.kind === "link" || action.kind === "map") {
      window.open(action.url, "_blank", "noopener,noreferrer");
    } else if (action.kind === "call") {
      window.location.href = action.url;
    }
  };

  // Both URLs go in the text field for reliable cross-platform share delivery.
  const handleShare = () => {
    const mapsAction = placeData.actions?.find((a) => a.kind === "map");
    const websiteAction = placeData.actions?.find((a) => a.kind === "link");
    const textParts: string[] = [];
    if (websiteAction?.url) textParts.push(websiteAction.url);
    if (mapsAction?.url) textParts.push(mapsAction.url);
    if (textParts.length === 0) textParts.push(window.location.href);
    if (navigator.share) {
      navigator
        .share({
          text: textParts.join("\n"),
        })
        .catch(() => {
          // User cancelled share
        });
    } else {
      showToast("Sharing not supported on this device", "info");
    }
  };

  const handleDeletePlace = async () => {
    if (!placeData.objectId) return;
    try {
      await api.objects.deleteObject(
        token ?? null,
        "place",
        placeData.objectId,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.byType("place"),
      });
      setShowDeleteConfirm(false);
      if (onClose) onClose();
    } catch (err) {
      console.error("[PlaceDetail] Failed to delete:", err);
      showToast("Failed to delete place.", "error");
    }
  };

  const openPhotoGallery = (index: number) => {
    setPhotoModalIndex(index);
    setCurrentView("photos");
  };

  const handleEditDescription = (currentDescription: string) => {
    setEditedDescription(currentDescription);
    setIsEditingDescription(true);
  };

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setEditedDescription("");
  };

  const handleSaveDescription = async () => {
    if (!placeData.objectId) {
      showToast("Cannot save: missing object ID", "error");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        objectType: "place",
        objectId: placeData.objectId,
        data: {
          description: editedDescription.trim(),
        },
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.detail(placeData.objectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(conversationId),
      });

      setIsEditingDescription(false);
    } catch (error) {
      console.error("[PlaceDetailModule] Failed to save description:", error);
      showToast("Failed to save description. Please try again.", "error");
    }
  };

  const handleSaveNotes = async () => {
    if (!placeData.objectId) return;
    try {
      await updateMutation.mutateAsync({
        objectType: "place",
        objectId: placeData.objectId,
        data: { attributes: { userNotes: editedNotes.trim() } },
      });
      // Update local config so the note shows immediately
      placeData.userNotes = editedNotes.trim();
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.detail(placeData.objectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(conversationId),
      });
      setIsEditingNotes(false);
    } catch (error) {
      console.error("[PlaceDetailModule] Failed to save notes:", error);
    }
  };

  const handleCopyPermalink = async () => {
    const url = (placeData as any).publish?.url || placeData.imageUrl;
    if (!url || !url.startsWith("https://")) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error("[PlaceDetailModule] Failed to copy permalink:", err);
    }
  };

  const galleryPhotos =
    placeData.galleryPhotos ||
    placeData.images?.filter((img) => !img.isMap) ||
    [];

  const websiteAction = placeData.actions?.find((a) => a.kind === "link");
  const directionsAction = placeData.actions?.find((a) => a.kind === "map");
  const callAction = placeData.actions?.find((a) => a.kind === "call");

  const isArtwork = placeData.isArtwork === true;
  const image = placeData.images?.[0];

  // Reviews query — MUST be at top level, not conditional (React hook rules).
  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    error: reviewsError,
  } = useQuery({
    queryKey: queryKeys.agent.enrichPlace(placeData.placeId || ""),
    queryFn: async () => {
      const { data } = await http.get<{
        placeName: string;
        rating: number | null;
        totalReviews: number;
        reviews: Array<{
          author: string;
          authorPhotoUri: string | null;
          rating: number;
          text: string;
          relativeTime: string;
          publishTime: string | null;
        }>;
      }>(`/places/${placeData.placeId}/reviews`, { authToken: token });
      return data;
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    enabled: !!placeData.placeId && currentView === "reviews",
  });

  // Fetch maps embed URL - MUST be at top level, not conditional.
  // The Address section's first item can arrive as a plain string, a
  // { value: string }, or a nested { value: { address } } / { address } object
  // depending on how the place was enriched — coerce to a display string so we
  // never render an object as a React child (#crash).
  const addrRaw = placeData.sections?.find((s) => s.title === "Address")
    ?.items?.[0] as unknown;
  const toAddressString = (v: unknown): string => {
    if (typeof v === "string") return v;
    if (v && typeof v === "object") {
      const o = v as Record<string, unknown>;
      if (typeof o.value === "string") return o.value;
      if (typeof o.address === "string") return o.address;
      if (o.value && typeof o.value === "object") {
        const inner = (o.value as Record<string, unknown>).address;
        if (typeof inner === "string") return inner;
      }
    }
    return "";
  };
  const address = toAddressString(addrRaw);
  const destination = address || placeData.subtitle || placeData.title;

  const { data: mapsData } = useQuery({
    queryKey: ["maps-embed-url", destination],
    queryFn: async () => {
      const { data } = await http.get<{ embedUrl: string }>(
        `/places/maps-embed-url`,
        { authToken: token, searchParams: { destination } },
      );
      return data;
    },
    enabled: !!destination && currentView === "directions",
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Auto-scroll thumbnail strip to show current photo
  React.useEffect(() => {
    if (currentView !== "photos") return;

    const currentThumbnail = thumbnailRefs.current[photoModalIndex];
    const stripContainer = thumbnailStripRef.current;

    if (currentThumbnail && stripContainer) {
      const thumbnailLeft = currentThumbnail.offsetLeft;
      const thumbnailWidth = currentThumbnail.offsetWidth;
      const containerWidth = stripContainer.offsetWidth;

      // Center the thumbnail in the strip.
      const scrollPosition =
        thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

      stripContainer.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [photoModalIndex, currentView]);

  // Photo keyboard nav — MUST be at top level, not conditional (React hook rules).
  React.useEffect(() => {
    if (currentView !== "photos" || galleryPhotos.length === 0) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && photoModalIndex > 0) {
        setPhotoModalIndex(photoModalIndex - 1);
      } else if (
        e.key === "ArrowRight" &&
        photoModalIndex < galleryPhotos.length - 1
      ) {
        setPhotoModalIndex(photoModalIndex + 1);
      } else if (e.key === "Escape") {
        if (onClose) {
          onClose();
        } else {
          setCurrentView("main");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentView, photoModalIndex, galleryPhotos.length, onClose]);

  // Minimal artwork rendering: just image and title
  if (isArtwork && image) {
    return (
      <div className="module">
        <div className="overflow-hidden rounded-lg shadow-md bg-gray-100 w-full">
          <div className="relative w-full aspect-[4/3] bg-gray-200">
            <img
              src={image.thumbnailUrl || image.fullsizeUrl}
              alt={placeData.title}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => openPhotoGallery(0)}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="p-3">
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
              {placeData.title}
            </h3>
            {placeData.subtitle && (
              <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                {placeData.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // PHOTOS VIEW - Photo gallery
  if (currentView === "photos" && galleryPhotos.length > 0) {
    return (
      <PlacePhotosView
        galleryPhotos={galleryPhotos}
        photoModalIndex={photoModalIndex}
        setPhotoModalIndex={setPhotoModalIndex}
        onBack={() => setCurrentView("main")}
        onClose={onClose}
        copiedLink={copiedLink}
        setCopiedLink={setCopiedLink}
        thumbnailStripRef={thumbnailStripRef}
        thumbnailRefs={thumbnailRefs}
        isMobile={isMobile}
      />
    );
  }

  // REVIEWS VIEW - Google Reviews
  if (currentView === "reviews") {
    return (
      <PlaceReviewsView
        reviewsData={reviewsData}
        reviewsLoading={reviewsLoading}
        reviewsError={reviewsError}
        placeTitle={placeData.title}
        onBack={() => setCurrentView("main")}
        isMobile={isMobile}
      />
    );
  }

  // DIRECTIONS VIEW - Interactive map with directions
  if (currentView === "directions") {
    return (
      <PlaceDirectionsView
        mapsEmbedUrl={mapsData?.embedUrl}
        address={address}
        destination={destination}
        placeTitle={placeData.title}
        onBack={() => setCurrentView("main")}
        isMobile={isMobile}
      />
    );
  }

  const aboutSection = placeData.sections?.find((s) => s.title === "About");
  const placeCardPhotos = galleryPhotos.map((p) => ({
    url: p.fullsizeUrl || p.thumbnailUrl || "",
    alt: p.caption,
  }));

  const handleAutoSaveNote = async (noteText: string) => {
    if (!placeData.objectId) {
      console.error("[PlaceDetailModule] No objectId — cannot save note");
      throw new Error("Cannot save note: place has no objectId");
    }
    try {
      const { apiPatch: doPatch } = await import("@/lib/api/client");
      await doPatch(
        `/places/${placeData.objectId}`,
        { userNotes: noteText.trim() },
        "*",
      );
      placeData.userNotes = noteText.trim();
      onNoteSaved?.(noteText.trim());
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.byType("place"),
      });
    } catch (error: any) {
      console.error(
        "[PlaceDetailModule] Failed to save note:",
        error?.message || error,
      );
      // Rethrow so PlaceCard's rejection path shows the error state
      // instead of flipping to "Saved" on a failed PATCH.
      throw error;
    }
  };

  // Extract neighborhood from subtitle (e.g., "Paris, 6e")
  const neighborhood =
    placeData.subtitle?.split(",").slice(-1)[0]?.trim() ||
    placeData.subtitle ||
    "";

  // Extract category and open status from chips
  const categoryChip = placeData.chips?.find(
    (c) =>
      !c.includes("Google reviews") &&
      !c.includes("Open") &&
      !c.includes("Closed") &&
      !c.includes("$"),
  );
  const priceLevelChip = placeData.chips?.find((c) => c.includes("$"));
  const isOpenChip = placeData.chips?.find(
    (c) => c.includes("Open") || c.includes("Closed"),
  );
  const ratingChip = placeData.chips?.find((c) => c.includes("Google reviews"));
  const parsedRating = ratingChip?.match(/⭐\s*([\d.]+)/)?.[1];
  const parsedReviewCount = ratingChip?.match(/\(([\d,]+)/)?.[1];

  // MAIN VIEW
  return (
    <PlaceCard
      name={placeData.title}
      category={categoryChip || undefined}
      priceLevel={priceLevelChip || undefined}
      rating={parsedRating ? parseFloat(parsedRating) : undefined}
      reviewCount={
        parsedReviewCount
          ? parseInt(parsedReviewCount.replace(/,/g, ""), 10)
          : undefined
      }
      isOpen={isOpenChip ? isOpenChip.includes("Open") : undefined}
      description={aboutSection?.body}
      neighborhood={neighborhood}
      photos={placeCardPhotos.length > 0 ? placeCardPhotos : [{ url: "" }]}
      note={placeData.userNotes}
      userRating={localRating}
      ratingSaved={ratingSaved}
      onNoteChange={handleAutoSaveNote}
      onRatingChange={(rating: number) => {
        if (!placeData.objectId) return;
        ratingTouchedRef.current = true;
        const newRating = rating > 0 ? rating : null;
        setLocalRating(newRating ?? undefined);
        setRatingSaved(false);
        // Debounce: update UI instantly, save after 500ms of no changes
        if (ratingSaveTimerRef.current)
          clearTimeout(ratingSaveTimerRef.current);
        ratingSaveTimerRef.current = setTimeout(async () => {
          try {
            const { apiPatch: doPatch } = await import("@/lib/api/client");
            await doPatch(
              `/places/${placeData.objectId}`,
              { userRating: newRating },
              "*",
            );
            setRatingSaved(true);
            setTimeout(() => setRatingSaved(false), 2000);
            queryClient.invalidateQueries({
              queryKey: queryKeys.objects.byType("place"),
            });
          } catch (err) {
            console.error("[PlaceDetailModule] Failed to save rating:", err);
          }
        }, 500);
      }}
      onDirectionsClick={
        directionsAction ? () => setCurrentView("directions") : undefined
      }
      onWebsiteClick={
        websiteAction ? () => handleAction(websiteAction) : undefined
      }
      onCallClick={callAction ? () => handleAction(callAction) : undefined}
    />
  );
}
